import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";

const StepCard = ({ step, isModified }) => {
    const [expanded, setExpanded] = useState(false);
    const Icon = expanded ? ChevronDown : ChevronRight;

    return (
        <div className={`border rounded-lg bg-white ${isModified ? "ring-2 ring-blue-400" : ""}`}>
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50"
            >
                <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 font-mono">#{step.order}</span>
                        <span className="font-medium text-sm truncate">{step.name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {step.type}
                        </span>
                        {step.language && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-600">
                                {step.language}
                            </span>
                        )}
                        {isModified && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                modified
                            </span>
                        )}
                    </div>
                    {step.description && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{step.description}</p>
                    )}
                </div>
                {step.code && (
                    <span className="text-xs text-slate-400 shrink-0">
                        {step.code.split("\n").length} lines
                    </span>
                )}
            </button>
            {expanded && step.code && (
                <div className="border-t p-3">
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-900 text-green-400 p-3 rounded max-h-[500px] overflow-auto">
                        {step.code}
                    </pre>
                </div>
            )}
            {expanded && !step.code && (
                <div className="border-t p-3">
                    <p className="text-sm text-slate-400 italic">No code</p>
                </div>
            )}
        </div>
    );
};

export default function StepsTab({ data }) {
    const { t } = useTranslation();
    const steps = data?.workflow?.steps || [];
    const modifiedIds = new Set(data?.debug?.modifiedSteps || []);

    if (steps.length === 0) {
        return <p className="text-muted-foreground text-sm p-4">{t("admin.automationPlayground.noSteps")}</p>;
    }

    return (
        <div className="space-y-2 mt-3">
            {steps.map((step) => (
                <StepCard key={step.id} step={step} isModified={modifiedIds.has(step.id)} />
            ))}
        </div>
    );
}
